import { useMemo } from "react";
import { extent, index, scaleLinear, scaleSqrt, sum } from "d3";
import { OpportunitySizeOption } from "../growth/utils";
import complexityColorScale from "../treemap/complexityColorScale";
import nodes from "./umap_ps_pruned.json";
import links from "./top_edges_pruned.json";
import { getFillColor } from "./utils";
import type { ProductSpaceNode, ProductData } from "./utils";
import type { ProcessedNode } from "./types";
import type { FindInVizOption } from "@atlas-int/controls";
import type { HierarchyNode } from "d3-hierarchy";
import type { ProductMetadatum } from "@atlas-int/data/types";
import { ColorBy } from "@atlas-int/data";

type ProductYearDatum = { productId: string; pci?: number; exportValue?: number; exportValueGrowth5?: number; [key: string]: unknown };
type CountryProductYearDatum = { productId: string; exportRca?: number; exportValue?: number; distance?: number; cog?: number; normalizedPci?: number; normalizedCog?: number; normalizedDistance?: number; year?: number; [key: string]: unknown };
type MetaDataNode = HierarchyNode<ProductMetadatum>;

interface UseProductSpaceDataParams {
  productYear: ProductYearDatum[];
  countryProductYear: CountryProductYearDatum[];
  metaDataLookup: Map<string, MetaDataNode>;
  productIdLookup: Map<string, MetaDataNode>;
  colorMap: Map<string, string>;
  clusterColorLookup: Map<string, { cluster_col?: string }>;
  colorBySelection: ColorBy;
  hiddenCategories: string[];
  sizing: string | undefined | null;
  rcaThreshold: number;
  productClass: string | undefined | null;
  width: number;
  height: number;
  xExtent: [number | undefined, number | undefined];
  yExtent: [number | undefined, number | undefined];
}

export function useProductSpaceData({
  productYear,
  countryProductYear,
  metaDataLookup,
  productIdLookup,
  colorMap,
  clusterColorLookup,
  colorBySelection,
  hiddenCategories,
  sizing,
  rcaThreshold,
  productClass,
  width,
  height,
  xExtent,
  yExtent,
}: UseProductSpaceDataParams) {
  const totalExportLookup: Map<string, ProductYearDatum> = useMemo(() => {
    return index(productYear, (d: ProductYearDatum) => d.productId);
  }, [productYear]);

  const countryExportLookup: Map<string, CountryProductYearDatum> = useMemo(() => {
    return index(countryProductYear, (d: CountryProductYearDatum) => d.productId);
  }, [countryProductYear]);

  const sizingExtent = useMemo(() => {
    const nodeLookup = index(nodes, (d) => d.product_code);

    const filterServices = (d: { productId: string; [key: string]: unknown }) => {
      const productData = metaDataLookup.get(d.productId)?.data;
      const node = nodeLookup.get(productData?.code as string);
      const topParentId = productData?.topParent?.productId;
      return topParentId?.split("-")[2] !== "14" && node;
    };
    if (sizing === OpportunitySizeOption.WorldTrade) {
      return extent(
        productYear.filter(filterServices),
        (d: ProductYearDatum) => d.exportValue || 0,
      );
    } else if (sizing === OpportunitySizeOption.CountryTrade) {
      return extent(
        countryProductYear.filter(filterServices),
        (d: CountryProductYearDatum) => d.exportValue || 0,
      );
    } else {
      return [0, 0];
    }
  }, [countryProductYear, metaDataLookup, productYear, sizing]);

  const { xScale, yScale, rScale } = useMemo(() => {
    const xScale = scaleLinear().domain(xExtent).range([0, width]);
    const yScale = scaleLinear().domain(yExtent).range([height, 0]);
    const rScale = scaleSqrt().domain(sizingExtent).range([2, 40]);
    return { xScale, yScale, rScale };
  }, [width, height, xExtent, yExtent, sizingExtent]);

  const linkLookup = useMemo(
    () =>
      links.reduce((obj: Record<string, string[]>, datum) => {
        const sourceId = productIdLookup.get(datum.source)?.data?.productId;
        const targetId = productIdLookup.get(datum.target)?.data?.productId;
        if (obj[sourceId]) {
          obj[sourceId] = Array.from(new Set([...obj[sourceId], targetId]));
        } else {
          obj[sourceId] = [targetId];
        }
        if (obj[targetId]) {
          obj[targetId] = Array.from(new Set([...obj[targetId], sourceId]));
        } else {
          obj[targetId] = [sourceId];
        }
        return obj;
      }, {}),
    [productIdLookup],
  );

  const processedData: ProcessedNode[] = useMemo(
    () =>
      (nodes as Array<{ product_code: string; x: string; y: string; total_exports?: string; cluster_name_short?: string; [key: string]: unknown }>)
        .map((node) => {
          const productData = productIdLookup.get(node.product_code)?.data;
          const topParentId = productData?.topParent?.productId;

          // Skip nodes without required data
          if (!productData || !topParentId) {
            return null;
          }

          let radiusValue: number | undefined;
          const { fill, filtered, fillColor } = getFillColor(
            node as ProductSpaceNode,
            productData as ProductData,
            topParentId as string,
            colorBySelection as ColorBy,
            colorMap,
            clusterColorLookup as Map<string, { cluster_col?: string }>,
            complexityColorScale as (value: number | undefined) => string,
            totalExportLookup as Map<string, { pci?: number }>,
            hiddenCategories,
            countryExportLookup as Map<string, { exportRca?: number; exportValue?: number }>,
            rcaThreshold as number,
          );

          if (sizing === OpportunitySizeOption.WorldTrade) {
            radiusValue = rScale(
              totalExportLookup.get(
                productIdLookup.get(node.product_code)?.data.productId,
              )?.exportValue,
            );
          } else if (sizing === OpportunitySizeOption.CountryTrade) {
            radiusValue = rScale(
              countryExportLookup.get(productData?.productId)?.exportValue,
            );
          } else {
            radiusValue = 4;
          }

          return {
            ...node,
            key: node.product_code,
            x: xScale(parseFloat(node.x)),
            y: yScale(parseFloat(node.y)),
            r: radiusValue || 4,
            fill,
            productId: productData?.productId,
            filtered,
            sector: metaDataLookup?.get(topParentId)?.data?.nameShortEn,
            fillColor,
          };
        })
        .filter((d): d is NonNullable<typeof d> => d !== null)
        .sort((a, b) => (b.r || 0) - (a.r || 0)),
    [
      clusterColorLookup,
      colorMap,
      countryExportLookup,
      colorBySelection,
      hiddenCategories,
      productIdLookup,
      rScale,
      sizing,
      totalExportLookup,
      xScale,
      yScale,
      rcaThreshold,
      metaDataLookup,
    ],
  );

  // Compute find-in-viz options (derived value)
  const findInVizOptions: FindInVizOption[] = useMemo(() => {
    return processedData
      .map((d) => metaDataLookup.get(d.productId))
      .filter((d): d is MetaDataNode => !!d?.data)
      .map((d) => ({
        id: d.data.productId,
        label: d.data.code
          ? `${d.data.nameShortEn} (${d.data.code} ${productClass})`
          : d.data.nameShortEn,
      }));
  }, [metaDataLookup, processedData, productClass]);

  // Compute total value (derived value)
  const totalValue = useMemo(() => {
    const value = sum(processedData, (d) => {
      const val = countryExportLookup.get(d?.productId)?.exportValue;
      return typeof val === "number" ? val : 0;
    });
    return value > 0 ? value : undefined;
  }, [countryExportLookup, processedData]);

  const nodeLookup = useMemo(() => {
    return index(
      processedData.filter((d) => d.productId),
      (d) => d.productId,
    );
  }, [processedData]);

  return {
    totalExportLookup,
    countryExportLookup,
    xScale,
    yScale,
    rScale,
    linkLookup,
    processedData,
    findInVizOptions,
    totalValue,
    nodeLookup,
  };
}
